import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_PIPELINE,
  IssueState,
  TERMINAL_RUN_STATUSES,
  type IssueDto,
  type UpdateIssueRequest,
} from '@aiai/shared';
import {
  createIssue,
  fetchIssue,
  fetchProject,
  runIssue,
  updateIssue,
} from '../api';
import { useImageUpload } from '../hooks/useImageUpload';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { projectNav } from '../lib/projectNav';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Callout } from '../components/Callout';
import { useCapabilities } from '../hooks/useCapabilities';
import {
  MarkdownEditor,
  type MarkdownEditorHandle,
} from '../components/MarkdownEditor';
import { PageHeader } from '../components/PageHeader';
import { Spinner } from '../components/Spinner';
import { ErrorState, LoadingState } from '../components/States';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useWorkspacePath } from '../hooks/useWorkspacePath';

type Mode = 'create' | 'edit';
/** What creating an issue should kick off. No silent "draft" — every created
 *  issue either goes to the PM to plan, or straight to the Engineer to build. */
type CreateIntent = 'pm' | 'build';

type FieldErrors = { body?: string };

export function IssueEditorPage({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { wp } = useWorkspacePath();
  const { can } = useCapabilities();
  const queryClient = useQueryClient();
  const params = useParams();
  const routeProjectId = params.projectId ?? '';
  const issueId = params.issueId ?? '';

  const prefillTitle = (location.state as { prefillTitle?: string } | null)?.prefillTitle ?? '';
  const prefillBody = (location.state as { prefillBody?: string } | null)?.prefillBody ?? '';

  // ── Load existing issue (edit) ──────────────────────────────────────────--
  const issueQuery = useQuery({
    queryKey: qk.issue(issueId),
    queryFn: () => fetchIssue(issueId),
    enabled: mode === 'edit' && !!issueId,
  });

  // Fetch the project code-first: in create mode the route param is already
  // the code; in edit mode prefer the issue's denormalized projectCode over
  // its UUID. (routeProjectId still seeds the create payload below.)
  const projectRef =
    mode === 'edit'
      ? (issueQuery.data?.projectCode ?? issueQuery.data?.projectId ?? '')
      : routeProjectId;

  const projectQuery = useQuery({
    queryKey: qk.project(projectRef),
    queryFn: () => fetchProject(projectRef),
    enabled: !!projectRef,
  });

  useDocumentTitle(
    mode === 'create'
      ? 'New issue'
      : issueQuery.data
        ? `Edit · ${issueQuery.data.title}`
        : 'Edit issue',
  );

  // ── Form state ──────────────────────────────────────────────────────────--
  const [body, setBody] = useState('');
  const hasPrefill = mode === 'create' && !!(prefillTitle || prefillBody);
  const [seeded, setSeeded] = useState(mode === 'create' && !hasPrefill);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Seed from router state in create mode (pre-fill from chat proposal card).
  useEffect(() => {
    if (mode === 'create' && hasPrefill && !seeded) {
      const content =
        prefillTitle && prefillBody
          ? `# ${prefillTitle}\n\n${prefillBody}`
          : prefillBody || `# ${prefillTitle}`;
      setBody(content);
      setSeeded(true);
    }
  }, [mode, hasPrefill, seeded, prefillTitle, prefillBody]);

  // Seed the form once the issue loads (edit mode).
  useEffect(() => {
    if (mode === 'edit' && issueQuery.data && !seeded) {
      const issue = issueQuery.data;
      setBody(issue.body);
      setSeeded(true);
    }
  }, [mode, issueQuery.data, seeded]);

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (body.trim().length < 10) next.body = 'Brief must be at least 10 characters.';
    setFieldErrors(next);
    return !next.body;
  };

  // ── Persistence helpers ─────────────────────────────────────────────────--
  const persist = async (intent: CreateIntent = 'build'): Promise<IssueDto> => {
    if (mode === 'create') {
      return createIssue({
        projectId: routeProjectId,
        body,
        pipeline: [...DEFAULT_PIPELINE],
        // Only 'build' approves it (→ state open → the Engineer builds the
        // brief verbatim). 'pm' files it proposed — the PM plans every
        // proposed issue and, on autopilot, its plan releases the issue to
        // the Engineer; on a paused project it waits for your Approve.
        approved: intent === 'build',
      });
    }
    const existingPipeline = issueQuery.data?.pipeline?.length
      ? issueQuery.data.pipeline
      : [...DEFAULT_PIPELINE];
    const payload: UpdateIssueRequest = {
      body,
      pipeline: existingPipeline,
    };
    return updateIssue(issueId, payload);
  };

  const invalidate = (issue: IssueDto) => {
    // The detail page caches the issue under its code (canonicalized route
    // param), so invalidate by code — a UUID key would miss it.
    queryClient.invalidateQueries({ queryKey: qk.issue(issue.code ?? issue.id) });
    queryClient.invalidateQueries({
      queryKey: qk.projectIssues(issue.projectId),
    });
  };

  const saveMutation = useMutation({
    mutationFn: (intent?: CreateIntent) => persist(intent),
    onSuccess: (issue) => {
      invalidate(issue);
      navigate(wp(`/issues/${issue.code ?? issue.id}`));
    },
    onError: (err) => setFormError(errorMessage(err, 'Could not save issue')),
  });

  const handoffMutation = useMutation({
    mutationFn: async () => {
      const issue = await persist();
      await updateIssue(issue.code ?? issue.id, { approved: true });
      const run = await runIssue(issue.code ?? issue.id);
      return { issue, runId: run.id };
    },
    onSuccess: ({ issue, runId }) => {
      invalidate(issue);
      navigate(wp(`/runs/${runId}`));
    },
    onError: (err) => setFormError(errorMessage(err, 'Could not save and run')),
  });

  const busy = saveMutation.isPending || handoffMutation.isPending;
  const editorRef = useRef<MarkdownEditorHandle>(null);

  const insertAtCursor = useCallback((snippet: string) => {
    // Images (the only current caller) belong on their own line.
    if (editorRef.current) {
      editorRef.current.insertAtCursor('\n' + snippet + '\n');
    } else {
      setBody((prev) => prev + snippet);
    }
  }, []);

  const { uploading: imageUploading, uploadError: imageUploadError, onPaste, onDrop, onDragOver } =
    useImageUpload({ onInsert: insertAtCursor });

  const hasActiveRun = (issue: IssueDto | undefined): boolean => {
    const latest = issue?.latestRun;
    return !!latest && !TERMINAL_RUN_STATUSES.includes(latest.status);
  };

  const guardActiveRun = (): boolean => {
    if (hasActiveRun(issueQuery.data)) {
      setFormError(
        'This issue already has an active run. Wait for it to finish before starting another.',
      );
      return false;
    }
    return true;
  };

  // Primary action (form submit / Enter): create → Create (files the ticket
  // with the team — the PM plans it, the Engineer builds it); edit → Save & run.
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    if (!validate() || !guardActiveRun()) return;
    if (mode === 'create') saveMutation.mutate('pm');
    else handoffMutation.mutate();
  };

  // Secondary action: create → the ✨ bypass (approve this exact brief and
  // build it right now, no PM turn); edit → Save (no run).
  const handleSecondary = () => {
    setFormError(null);
    if (!validate() || !guardActiveRun()) return;
    saveMutation.mutate(mode === 'create' ? 'build' : undefined);
  };

  // ── Loading / error gates ───────────────────────────────────────────────--
  if (mode === 'edit' && issueQuery.isLoading) {
    return <LoadingState label="Loading issue…" />;
  }
  if (mode === 'edit' && issueQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(issueQuery.error, 'Failed to load issue')}
        onRetry={() => issueQuery.refetch()}
      />
    );
  }
  if (mode === 'edit' && issueQuery.data?.state === IssueState.Closed) {
    return (
      <div className="space-y-4">
        <Callout tone="info">
          This ticket is closed and can no longer be edited.
        </Callout>
        <Link to={wp(`/issues/${issueId}`)} className="btn-secondary">
          Back to ticket
        </Link>
      </div>
    );
  }

  const nav = projectNav(projectQuery.data);
  const cancelTo = mode === 'edit' ? wp(`/issues/${issueId}`) : wp(nav.selfTo);

  // Capability gate: creating needs `issue:create`, editing needs `issue:edit`
  // on THIS project — checked against the project's UUID (grants are uuid-keyed,
  // and the route may carry a code) once the project has loaded. A viewer or
  // ungranted member is shown a clear denial instead of a form the server would
  // 403 on save.
  if (
    projectQuery.data &&
    !can(mode === 'create' ? 'issue:create' : 'issue:edit', {
      projectId: projectQuery.data.id,
    })
  ) {
    return (
      <div className="space-y-4">
        <Callout tone="warning">
          You don’t have permission to {mode === 'create' ? 'create issues in' : 'edit issues in'}{' '}
          this project. Ask a workspace owner for a contributor role.
        </Callout>
        <Link to={cancelTo} className="btn-secondary">
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: nav.rootLabel, to: wp(nav.rootTo) },
          { label: nav.name, to: wp(nav.selfTo) },
          ...(mode === 'edit'
            ? [
                {
                  label: issueQuery.data?.code
                    ? `${issueQuery.data.code} ${issueQuery.data.title}`
                    : (issueQuery.data?.title ?? 'Issue'),
                  to: wp(`/issues/${issueQuery.data?.code ?? issueId}`),
                },
                { label: 'Edit' },
              ]
            : [{ label: 'New issue' }]),
        ]}
      />

      <PageHeader title={mode === 'create' ? 'New issue' : 'Edit issue'} />

      {mode === 'edit' && hasActiveRun(issueQuery.data) && (
        <Callout tone="warning">
          A run is currently in progress — the agent received the previous
          version of this brief. You can review it now, but saving changes
          will not be possible until the run completes.
        </Callout>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        {/* Title is derived from the brief body (#629) — no title field or kind
            selector; the action buttons at the bottom are the choice. */}

        {/* Brief — the first and primary input: the title is derived from it, and
            it's the whole task. Obsidian-style live-preview editor: one pane,
            markdown renders as you type. */}
        <div>
          <span className="label mb-2 block">Brief</span>

          <MarkdownEditor
            ref={editorRef}
            value={body}
            onChange={setBody}
            ariaLabel="Brief (markdown)"
            placeholder="## Goal — describe what the agent should build…"
            onPaste={onPaste}
            onDrop={onDrop}
            onDragOver={onDragOver}
          />

          {imageUploading && (
            <p className="mt-1 text-xs text-ink-muted">Uploading image…</p>
          )}
          {imageUploadError && (
            <p className="mt-1 text-xs text-danger">{imageUploadError}</p>
          )}
          {fieldErrors.body && (
            <p role="alert" className="mt-1 text-xs text-danger-300">
              {fieldErrors.body}
            </p>
          )}
          <p className="mt-1 text-xs text-ink-faint">
            Paste or drop images to embed them.
          </p>
        </div>

        {formError && <Callout tone="danger">{formError}</Callout>}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => navigate(cancelTo)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={mode === 'create' ? 'btn-sparkle' : 'btn-secondary'}
            disabled={busy}
            onClick={handleSecondary}
            title={
              mode === 'create'
                ? 'Skip the PM — approve and build this brief exactly as written, right now'
                : 'Save changes'
            }
          >
            {saveMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
            {mode === 'create' ? (
              <>
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
                Just build it
              </>
            ) : (
              'Save'
            )}
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={busy}
            title={
              mode === 'create'
                ? projectQuery.data?.autonomy === 'manual'
                  ? 'File the ticket — the team is paused, so it waits for you'
                  : 'File the ticket with your Product Manager — it plans it and the Engineer builds it'
                : 'Save, mark ready, and enqueue an agent run'
            }
          >
            {(mode === 'create' ? saveMutation.isPending : handoffMutation.isPending) && (
              <Spinner className="h-3.5 w-3.5" />
            )}
            {mode === 'create' ? 'Create' : 'Save & run'}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </form>

    </div>
  );
}
